// NoContactCard.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  getDoc,
  where,
  query,
} from 'firebase/firestore';
import { differenceInDays } from 'date-fns';
import DashboardCard from './DashboardCard';
import { useAuth } from '../../../../contexts/AuthContext';
import { toast } from 'react-toastify';

// Configuration Flag: Set to true to use fake data
const USE_FAKE_DATA = true;
const TOTAL_FAKE_STUDENTS = 367;

const RISK_CATEGORIES = [
  { label: 'Very Low', color: '#e53935' },
  { label: 'Low', color: '#fb8c00' },
  { label: 'Moderate', color: '#fdd835' },
  { label: 'High', color: '#43a047' },
  { label: 'Very High', color: '#1e88e5' },
];

const generateFakeData = () => {
  const inContactCount = Math.floor(TOTAL_FAKE_STUDENTS * 0.6); // 60% contacted
  const noContactCount = TOTAL_FAKE_STUDENTS - inContactCount;
  const riskDistribution = {
    'Very Low': Math.floor(noContactCount * 0.08),
    'Low': Math.floor(noContactCount * 0.3),
    'Moderate': Math.floor(noContactCount * 0.37),
    'High': Math.floor(noContactCount * 0.15),
    'Very High':
      noContactCount -
      (Math.floor(noContactCount * 0.08) +
        Math.floor(noContactCount * 0.3) +
        Math.floor(noContactCount * 0.37) +
        Math.floor(noContactCount * 0.15)),
  };

  return {
    totalStudents: TOTAL_FAKE_STUDENTS,
    inContactCount,
    noContactCount,
    categoryData: RISK_CATEGORIES.map((rc) => ({
      ...rc,
      count: riskDistribution[rc.label] || 0,
    })),
  };
};

const NoContactCard = forwardRef(({ onHide }, ref) => {
  const { currentUser } = useAuth();
  const db = getFirestore();

  // Toggle between "my students" vs "all students"
  const [myStudentsOnly, setMyStudentsOnly] = useState(true);
  // Day threshold for contact status
  const [daysThreshold, setDaysThreshold] = useState(30);
  // Loading & data states
  const [isLoading, setIsLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);
  const [inContactCount, setInContactCount] = useState(0);
  const [noContactCount, setNoContactCount] = useState(0);
  const [categoryData, setCategoryData] = useState(
    RISK_CATEGORIES.map((rc) => ({ ...rc, count: 0 }))
  );

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchData();
    },
  }));

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    if (USE_FAKE_DATA) {
      const fakeData = generateFakeData();
      setTotalStudents(fakeData.totalStudents);
      setInContactCount(fakeData.inContactCount);
      setNoContactCount(fakeData.noContactCount);
      setCategoryData(fakeData.categoryData);
      console.log('[NoContactCard] Using fake data:', fakeData);
      setIsLoading(false);
      return;
    }

    if (!currentUser?.uid) {
      setIsLoading(false);
      return;
    }

    try {
      console.log(
        '[NoContactCard] Checking contact status:',
        `myStudentsOnly=${myStudentsOnly}, daysThreshold=${daysThreshold}`
      );
      const studentAdvRef = collection(db, 'studentAdvisors');
      let qRef;
      if (myStudentsOnly) {
        qRef = query(
          studentAdvRef,
          where('advisorIds', 'array-contains', currentUser.uid)
        );
      } else {
        qRef = studentAdvRef;
      }
      const snap = await getDocs(qRef);
      console.log(`[NoContactCard] Found ${snap.size} docs in studentAdvisors.`);
      const studentIds = snap.docs.map((docSnap) => docSnap.id);
      setTotalStudents(studentIds.length);

      if (studentIds.length === 0) {
        setInContactCount(0);
        setNoContactCount(0);
        setCategoryData(RISK_CATEGORIES.map((rc) => ({ ...rc, count: 0 })));
        setIsLoading(false);
        return;
      }

      // Fetch all user docs in parallel
      const userDocPromises = studentIds.map((id) =>
        getDoc(doc(db, 'users', id))
      );
      const userDocs = await Promise.all(userDocPromises);

      // Fetch all persistence docs in parallel (for consistency)
      const persistDocPromises = studentIds.map((id) =>
        getDoc(doc(db, 'Persistence', id))
      );
      await Promise.all(persistDocPromises);

      let contacted = 0;
      let notContacted = 0;
      const newCounts = {
        'Very Low': 0,
        Low: 0,
        Moderate: 0,
        High: 0,
        'Very High': 0,
      };

      for (let i = 0; i < studentIds.length; i++) {
        const studentId = studentIds[i];
        const userDocSnap = userDocs[i];

        let lastContact = null;
        const studentAdvData = snap.docs[i].data();
        if (studentAdvData.lastContactTimestamps?.[currentUser.uid]) {
          lastContact =
            studentAdvData.lastContactTimestamps[currentUser.uid].toDate();
        }

        let daysSince = Infinity;
        if (lastContact) {
          daysSince = differenceInDays(new Date(), lastContact);
        }

        let riskLevel = 'Moderate';
        if (userDocSnap.exists()) {
          const uData = userDocSnap.data();
          if (uData.riskLevel) {
            riskLevel = uData.riskLevel;
          }
        }

        if (daysSince <= daysThreshold) {
          contacted++;
        } else {
          notContacted++;
          if (newCounts[riskLevel] !== undefined) {
            newCounts[riskLevel]++;
          } else {
            console.log(
              `[NoContactCard] Unknown riskLevel: ${riskLevel} for student ${studentId}`
            );
          }
        }
      }

      const mergedCategoryData = RISK_CATEGORIES.map((rc) => ({
        ...rc,
        count: newCounts[rc.label] || 0,
      }));

      setInContactCount(contacted);
      setNoContactCount(notContacted);
      setCategoryData(mergedCategoryData);

      console.log(
        `Total: ${studentIds.length}, InContact: ${contacted}, NoContact: ${notContacted}`
      );
    } catch (err) {
      console.error('[NoContactCard] Error:', err);
      toast.error('Failed to fetch contact status.');
    }
    setIsLoading(false);
  }, [myStudentsOnly, daysThreshold, currentUser?.uid, db]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate the fill percentage for the progress bar
  const portion =
    totalStudents === 0 ? 0 : (inContactCount / totalStudents) * 100;

  // Set text colors conditionally based on fill width
  const leftTextClass = portion > 25 ? 'text-white' : 'text-gray-900';
  const rightTextClass = portion < 75 ? 'text-gray-900' : 'text-white';

  const handleDaysChange = (e) => setDaysThreshold(Number(e.target.value));
  const handleToggleStudents = () => {
    setMyStudentsOnly((prev) => !prev);
  };

  return (
    <DashboardCard
      title={<span className="font-medium">Contact Status</span>}
      extraButtons={
        <button
          onClick={handleToggleStudents}
          className="text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          {myStudentsOnly ? 'Show All Students' : 'Show My Students'}
        </button>
      }
      onHide={onHide}
      isLoading={isLoading}
    >
      {!isLoading && (
        <div className="space-y-2">
          {/* Inline header with dropdown */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Students contacted within
            </span>
            <select
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-1 w-20"
              onChange={handleDaysChange}
              value={daysThreshold}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>

          {/* Progress bar with overlayed counts and conditional text colors */}
          <div className="relative w-full bg-gray-200 rounded-full h-5">
            <div
              className="bg-blue-600 h-5 rounded-full"
              style={{ width: `${portion}%` }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-between px-2 text-xs font-semibold">
              <span className={leftTextClass}>
                Contacted: {inContactCount}
              </span>
              <span className={rightTextClass}>
                No-Contact: {noContactCount}
              </span>
            </div>
          </div>

          {/* Risk-level breakdown */}
          <div className="space-y-1 w-full bg-white dark:bg-gray-800
                           border border-gray-300 dark:border-gray-700
                           rounded-lg p-2 flex flex-col justify-between
                           relative transition-all duration-300">
            {categoryData.map((cat) => {
              const catPortion =
                noContactCount === 0 ? 0 : (cat.count / noContactCount) * 100;
              return (
                <div key={cat.label} className="text-xs">
                  <div className="flex justify-between">
                    <span>{cat.label}</span>
                    <span className="text-gray-500">
                      {cat.count}/{noContactCount}
                    </span>
                  </div>
                  <div className="relative bg-gray-200 rounded-full h-3">
                    <div
                      className="rounded-full h-3"
                      style={{
                        width: `${catPortion}%`,
                        backgroundColor: cat.color,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DashboardCard>
  );
});

export default NoContactCard;
